import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  styled
} from '@mui/material';
import { useRef, useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import ExpandMoreTwoToneIcon from '@mui/icons-material/ExpandMoreTwoTone';
import { useAuth } from 'src/contexts/AuthContext';

// Category color mappings (copied from SidebarMenu)
const iconColors = {
  overview: '#b0b4ba',
  dashboards: '#1975ff',
  fitness: '#9c27b0',
  budget: '#43a047',
  stocks: '#1b5e20',
  components: '#0288d1',
  extra: '#00bcd4',
  parcel: '#ffa000',
  parts: '#e53935',
  trip: '#ff7043',
  identity: '#fff8c4',
  cv: '#00695c'
};

const categoryHoverColors = {
  overview: 'rgba(176, 180, 186, 0.13)',
  dashboards: 'rgba(25, 117, 255, 0.13)',
  fitness: 'rgba(156, 39, 176, 0.13)',
  budget: 'rgba(67, 160, 71, 0.13)',
  stocks: 'rgba(27, 94, 32, 0.13)',
  components: 'rgba(2, 136, 209, 0.13)',
  extra: 'rgba(0, 188, 212, 0.13)',
  parcel: 'rgba(255, 160, 0, 0.13)',
  parts: 'rgba(229, 57, 53, 0.13)',
  trip: 'rgba(255, 112, 67, 0.13)',
  identity: 'rgba(255, 248, 196, 0.2)',
  cv: 'rgba(0, 105, 92, 0.13)'
};

// Helper: get category by route
function getCategoryByRoute(route) {
  if (route.startsWith('/overview') || route.startsWith('/home') || route.startsWith('/settings')) return 'overview';
  if (route.startsWith('/cv') || route.startsWith('/profile')) return 'cv';
  if (route === '/dashboards/trip-reports') return 'trip';
  if (route.startsWith('/dashboards')) return 'dashboards';
  if (route.startsWith('/management/profile')) return 'accounts';
  if (route.startsWith('/management')) return 'management';
  if (route.startsWith('/accounts')) return 'accounts';
  if (route.startsWith('/users/management')) return 'identity';
  if (route.startsWith('/users/identities')) return 'identity';
  if (route.startsWith('/identity')) return 'identity';
  if (route.startsWith('/components')) return 'components';     
  if (route.startsWith('/trip')) return 'trip';
  if (route.startsWith('/parcel')) return 'parcel';
  if (route.startsWith('/parts')) return 'parts';
  if (route.startsWith('/budget')) return 'budget';
  if (route.startsWith('/stocks')) return 'stocks';
  if (route.startsWith('/fitness/')) return 'fitness';
  if (route === '/fitness') return 'fitness';
  if (route.startsWith('/training')) return 'training';
  return 'extra';
}

const ListWrapper = styled(Box)(
  ({ theme }) => `
    .MuiList-root {
      padding: ${theme.spacing(1)};
    }
    .MuiListItem-root {
      margin: ${theme.spacing(0, 0.5)};
      padding: ${theme.spacing(1, 2)};
      transition: ${theme.transitions.create(['color', 'fill'])};

      &.MuiListItem-indicators {
        .MuiListItemText-root {
          .MuiTypography-root {
            &:before {
              height: 4px;
              width: 22px;
              opacity: 0;
              visibility: hidden;
              display: block;
              position: absolute;
              bottom: -10px;
              transition: all .2s;
              border-radius: ${theme.general.borderRadiusLg};
              content: "";
              background: currentColor;
            }
          }
        }

        &:hover,
        &.active {
          .MuiListItemText-root {
            .MuiTypography-root {
              &:before {
                opacity: 1;
                visibility: visible;
                bottom: 0px;
              }
            }
          }
        }
      }
    }
  `
);

function HeaderMenu() {
  // Stocks submenu state (match other menus)
  const refStocks = useRef(null);
  const [isOpenStocks, setOpenStocks] = useState(false);
  const handleOpenStocks = () => setOpenStocks(true);
  const handleCloseStocks = () => setOpenStocks(false);
  const { user, isAdmin } = useAuth();
  
  // Suppress MUI default selected/active background for MenuItem in submenus
  // Only suppress default selected/active background, but allow hover effect for active submenu items
  const menuItemNoActiveStyle = {
    '&.Mui-selected, &.active': {
      backgroundColor: 'inherit !important',
      color: 'inherit !important',
    }
  };
  const ref = useRef(null);
  const [isOpen, setOpen] = useState(false);
  const refParcel = useRef(null);
  const [isOpenParcel, setOpenParcel] = useState(false);
  // Parts Management menu state
  // Home menu state
  const refHome = useRef(null);
  const [isOpenHome, setOpenHome] = useState(false);
  const handleOpenHome = () => setOpenHome(true);
  const handleCloseHome = () => setOpenHome(false);
  // Identity Management menu state
  const refIdentity = useRef(null);
  const [isOpenIdentity, setOpenIdentity] = useState(false);
  const handleOpenIdentity = () => setOpenIdentity(true);
  const handleCloseIdentity = () => setOpenIdentity(false);
  const refParts = useRef(null);
  const [isOpenParts, setOpenParts] = useState(false);
  // Budget Management menu state
  const refBudget = useRef(null);
  const [isOpenBudget, setOpenBudget] = useState(false);
  // Training menu state
  const refTraining = useRef(null);
  const [isOpenTraining, setOpenTraining] = useState(false);
  // Profile menu state (separate from Travel)
  const refProfile = useRef(null);
  const [isOpenProfile, setOpenProfile] = useState(false);
  const handleOpenProfile = () => setOpenProfile(true);
  const handleCloseProfile = () => setOpenProfile(false);
  const location = useLocation();

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleOpenParcel = () => setOpenParcel(true);
  const handleCloseParcel = () => setOpenParcel(false);
  // Parts Management menu handlers
  const handleOpenParts = () => setOpenParts(true);
  const handleCloseParts = () => setOpenParts(false);
  const handleOpenBudget = () => setOpenBudget(true);
  const handleCloseBudget = () => setOpenBudget(false);
  const handleOpenTraining = () => setOpenTraining(true);
  const handleCloseTraining = () => setOpenTraining(false);
  useEffect(() => { handleClose(); handleCloseParcel(); handleCloseParts(); handleCloseBudget(); handleCloseStocks(); handleCloseTraining(); handleCloseIdentity(); }, [location.pathname]);
  useEffect(() => { handleCloseHome(); }, [location.pathname]);

  const getMenuItemStyle = (to) => {
    const category = getCategoryByRoute(to);
    return {
      px: 3,
      '&&:hover': {
        backgroundColor: `${categoryHoverColors[category]} !important`,
        color: `${iconColors[category]} !important`,
      },
      '&&.Mui-selected': {
        backgroundColor: `${categoryHoverColors[category]} !important`,
        color: `${iconColors[category]} !important`,
      },
      '&&.Mui-selected:hover': {
        backgroundColor: `${categoryHoverColors[category]} !important`,
        color: `${iconColors[category]} !important`,
      },
      transition: 'background-color 0.2s, color 0.2s',
    };
  };
  const getListItemStyle = (to) => {
    const category = getCategoryByRoute(to);
    return {
      '&:hover, &.active': {
        backgroundColor: categoryHoverColors[category],
        color: iconColors[category],
      },
      transition: 'background-color 0.2s, color 0.2s',
    };
  };

  return (
    <>
      <ListWrapper sx={{ display: { xs: 'none', md: 'block' } }}>
        <List disablePadding component={Box} display="flex" sx={{ gap: 0.5, ml: 2 }}>
          {/* Home menu - Overview always visible, Settings only for admin */}
          <>
            <ListItemButton
              classes={{ root: 'MuiListItem-indicators' }}
              ref={refHome}
              onClick={handleOpenHome}
              sx={getListItemStyle('/overview')}
            >
              <ListItemText
                primaryTypographyProps={{ noWrap: true }}
                primary={
                  <Box display="flex" alignItems="center">
                    Home
                    <Box display="flex" alignItems="center" pl={0.3}>
                      <ExpandMoreTwoToneIcon fontSize="small" />
                    </Box>
                  </Box>
                }
              />
            </ListItemButton>
            {/* Home submenu */}
            <Menu 
              anchorEl={refHome.current} 
              onClose={handleCloseHome} 
              open={isOpenHome}
              keepMounted={false}
              disableScrollLock
              disablePortal={false}
              PaperProps={{
                sx: {
                  marginTop: 2,
                  zIndex: theme => theme.zIndex.drawer + 100,
                  '& .MuiList-root': {
                    padding: 1
                  }
                }
              }}
            >
              <MenuItem 
                component={NavLink} 
                to="/home" 
                sx={{ 
                    px: 3, 
                    ...getMenuItemStyle('/overview'), 
                    ...menuItemNoActiveStyle 
                  }}>
                  Overview
              </MenuItem>

              {!user && (
                <MenuItem 
                  component={NavLink} 
                  to="/login" 
                  sx={{ 
                      px: 3, 
                      ...getMenuItemStyle('/overview'), 
                      ...menuItemNoActiveStyle 
                    }}>
                    Login
                </MenuItem>
              )}

              {user && isAdmin && (
                <MenuItem 
                  component={NavLink} 
                  to="/settings" 
                  sx={{ 
                    px: 3, 
                    ...getMenuItemStyle('/settings'), 
                    ...menuItemNoActiveStyle 
                  }}>
                    Settings
                </MenuItem>
              )}
            </Menu>
          </>

          {/* Identity Management - only show for admin users, between Home and Travel menus */}
          {isAdmin && (
            <>
              <ListItemButton
                classes={{ root: 'MuiListItem-indicators' }}
                ref={refIdentity}
                onClick={handleOpenIdentity}
                sx={getListItemStyle('/users/management')}
              >
                <ListItemText
                  primaryTypographyProps={{ noWrap: true }}
                  primary={
                    <Box display="flex" alignItems="center">
                      Admin
                      <Box display="flex" alignItems="center" pl={0.3}>
                        <ExpandMoreTwoToneIcon fontSize="small" />
                      </Box>
                    </Box>
                  }
                />
              </ListItemButton>
              {/* Identity Management submenu - only show for admin users, right after the button */}
              {user && (
                <Menu 
                  anchorEl={refIdentity.current} 
                  onClose={handleCloseIdentity} 
                  open={isOpenIdentity}
                  keepMounted={false}
                  disableScrollLock
                  disablePortal={false}
                  PaperProps={{
                    sx: {
                      marginTop: 2,
                      zIndex: theme => theme.zIndex.drawer + 100,
                      '& .MuiList-root': {
                        padding: 1
                      }
                    }
                  }}
                >
                  <MenuItem 
                    component={NavLink} 
                    to="/users/management" 
                    sx={{ px: 3, ...getMenuItemStyle('/users/management'), ...menuItemNoActiveStyle }}>
                      Users
                    </MenuItem>
                  <MenuItem 
                    component={NavLink} 
                    to="/users/identities" 
                    sx={{ px: 3, ...getMenuItemStyle('/users/identities'), ...menuItemNoActiveStyle }}>
                      Identities
                    </MenuItem>
                </Menu>
              )}
            </>
          )}

          {/* Protected menu items - only show if user is logged in */}
          {user && (
            <>
              {/* Travel Management */}
              <ListItemButton
                classes={{ root: 'MuiListItem-indicators' }}
                ref={ref}
                onClick={handleOpen}
                sx={getListItemStyle('/trip/trips')}
              >
            <ListItemText
              primaryTypographyProps={{ noWrap: true }}
              primary={
                <Box display="flex" alignItems="center">
                  Travel
                  <Box display="flex" alignItems="center" pl={0.3}>
                    <ExpandMoreTwoToneIcon fontSize="small" />
                  </Box>
                </Box>
              }
            />
          </ListItemButton>

          {/* Parcel Management */}
          <ListItemButton
            classes={{ root: 'MuiListItem-indicators' }}
            ref={refParcel}
            onClick={handleOpenParcel}
            sx={getListItemStyle('/parcel/shipments')}
          >
            <ListItemText
              primaryTypographyProps={{ noWrap: true }}
              primary={
                <Box display="flex" alignItems="center">
                  Parcels
                  <Box display="flex" alignItems="center" pl={0.3}>
                    <ExpandMoreTwoToneIcon fontSize="small" />
                  </Box>
                </Box>
              }
            />
          </ListItemButton>

          {/* Parts Management */}
          <ListItemButton
            classes={{ root: 'MuiListItem-indicators' }}
            ref={refParts}
            onClick={handleOpenParts}
            sx={getListItemStyle('/parts/assemblies')}
          >
            <ListItemText
              primaryTypographyProps={{ noWrap: true }}
              primary={
                <Box display="flex" alignItems="center">
                  Parts
                  <Box display="flex" alignItems="center" pl={0.3}>
                    <ExpandMoreTwoToneIcon fontSize="small" />
                  </Box>
                </Box>
              }
            />
          </ListItemButton>
          {/* Fitness Training (moved before Budget) */}
          <ListItemButton
            classes={{ root: 'MuiListItem-indicators' }}
            ref={refTraining}
            onClick={handleOpenTraining}
            sx={getListItemStyle('/fitness/workouts')}
          >
            <ListItemText
              primaryTypographyProps={{ noWrap: true }}
              primary={
                <Box display="flex" alignItems="center">
                  Fitness
                  <Box display="flex" alignItems="center" pl={0.3}>
                    <ExpandMoreTwoToneIcon fontSize="small" />
                  </Box>
                </Box>
              }
            />
          </ListItemButton>

          {/* Budget Management (now after Fitness) */}
          <ListItemButton
            classes={{ root: 'MuiListItem-indicators' }}
            ref={refBudget}
            onClick={handleOpenBudget}
            sx={getListItemStyle('/budget/dashboard')}
          >
            <ListItemText
              primaryTypographyProps={{ noWrap: true }}
              primary={
                <Box display="flex" alignItems="center">
                  Budget
                  <Box display="flex" alignItems="center" pl={0.3}>
                    <ExpandMoreTwoToneIcon fontSize="small" />
                  </Box>
                </Box>
              }
            />
          </ListItemButton>

          {/* Stocks Management */}
          <ListItemButton
            classes={{ root: 'MuiListItem-indicators' }}
            ref={refStocks}
            onClick={handleOpenStocks}
            sx={getListItemStyle('/stocks/stocks')}
          >
            <ListItemText
              primaryTypographyProps={{ noWrap: true }}
              primary={
                <Box display="flex" alignItems="center">
                  Stocks
                  <Box display="flex" alignItems="center" pl={0.3}>
                    <ExpandMoreTwoToneIcon fontSize="small" />
                  </Box>
                </Box>
              }
            />
          </ListItemButton>
            {/* Profile dropdown trigger */}
            <ListItemButton
              classes={{ root: 'MuiListItem-indicators' }}
              ref={refProfile}
              onClick={handleOpenProfile}
              sx={getListItemStyle('/cv/cv')}
            >
              <ListItemText
                primaryTypographyProps={{ noWrap: true }}
                primary={
                  <Box display="flex" alignItems="center">
                    Profile
                    <Box display="flex" alignItems="center" pl={0.3}>
                      <ExpandMoreTwoToneIcon fontSize="small" />
                    </Box>
                  </Box>
                }
              />
            </ListItemButton>


            {/* Profile submenu */}
            <Menu
              anchorEl={refProfile.current}
              open={isOpenProfile}
              onClose={handleCloseProfile}
              keepMounted={false}
              disableScrollLock
              disablePortal={false}
              PaperProps={{
                sx: {
                  marginTop: 2,
                  zIndex: theme => theme.zIndex.drawer + 100,
                  '& .MuiList-root': {
                    padding: 1
                  }
                }
              }}
            >
              <MenuItem component={NavLink} to="/profile/page" sx={{ px: 3, ...getMenuItemStyle('/profile/page'), ...menuItemNoActiveStyle }} onClick={handleCloseProfile}>Page</MenuItem>
              <MenuItem component={NavLink} to="/profile/external" sx={{ px: 3, ...getMenuItemStyle('/profile/external'), ...menuItemNoActiveStyle }} onClick={handleCloseProfile}>External</MenuItem>
              <MenuItem component={NavLink} to="/profile/site" sx={{ px: 3, ...getMenuItemStyle('/profile/site'), ...menuItemNoActiveStyle }} onClick={handleCloseProfile}>Site</MenuItem>
              <MenuItem component={NavLink} to="/profile/certifications" sx={{ px: 3, ...getMenuItemStyle('/profile/certifications'), ...menuItemNoActiveStyle }} onClick={handleCloseProfile}>Certifications</MenuItem>
              <MenuItem component={NavLink} to="/profile/academic" sx={{ px: 3, ...getMenuItemStyle('/profile/academic'), ...menuItemNoActiveStyle }} onClick={handleCloseProfile}>Academic</MenuItem>
              <MenuItem component={NavLink} to="/profile/work" sx={{ px: 3, ...getMenuItemStyle('/profile/work'), ...menuItemNoActiveStyle }} onClick={handleCloseProfile}>Work</MenuItem>
              <MenuItem component={NavLink} to="/profile/projects" sx={{ px: 3, ...getMenuItemStyle('/profile/projects'), ...menuItemNoActiveStyle }} onClick={handleCloseProfile}>Projects</MenuItem>
              <MenuItem component={NavLink} to="/profile/versions" sx={{ px: 3, ...getMenuItemStyle('/profile/versions'), ...menuItemNoActiveStyle }} onClick={handleCloseProfile}>Versions</MenuItem>
              <MenuItem component={NavLink} to="/profile/tags" sx={{ px: 3, ...getMenuItemStyle('/profile/tags'), ...menuItemNoActiveStyle }} onClick={handleCloseProfile}>Tags</MenuItem>
              <MenuItem component={NavLink} to="/profile/catalog" sx={{ px: 3, ...getMenuItemStyle('/profile/catalog'), ...menuItemNoActiveStyle }} onClick={handleCloseProfile}>Catalog</MenuItem>
            </Menu>
          
            </>
          )}

          {/* Public menu items */}
         {/*  <ListItemButton
            classes={{ root: 'MuiListItem-indicators' }}
            
            component={NavLink}
            to="/crypto"
            sx={getListItemStyle('/crypto')}
          >
            <ListItemText
              primaryTypographyProps={{ noWrap: true }}
              primary="Crypto"
            />
          </ListItemButton> */}
        </List>
      </ListWrapper>

      {/* Travel Management submenu */}
      {user && (
        <Menu 
          anchorEl={ref.current} 
          onClose={handleClose} 
          open={isOpen}
          keepMounted={false}
          disableScrollLock
          disablePortal={false}
          PaperProps={{
            sx: {
              marginTop: 2,
              zIndex: theme => theme.zIndex.drawer + 100,
              '& .MuiList-root': {
                padding: 1
              }
            }
          }}
        >
          <MenuItem component={NavLink} to="/trip/trips" sx={{ px: 3, ...getMenuItemStyle('/trip/trips'), ...menuItemNoActiveStyle }}>Trips</MenuItem>
          <MenuItem component={NavLink} to="/trip/changes" sx={{ px: 3, ...getMenuItemStyle('/trip/changes'), ...menuItemNoActiveStyle }}>Changes</MenuItem>
          <MenuItem component={NavLink} to="/trip/reservations" sx={{ px: 3, ...getMenuItemStyle('/trip/reservations'), ...menuItemNoActiveStyle }}>Reservations</MenuItem>
          <MenuItem component={NavLink} to="/trip/travelers" sx={{ px: 3, ...getMenuItemStyle('/trip/travelers'), ...menuItemNoActiveStyle }}>Travelers</MenuItem>
          <MenuItem component={NavLink} to="/trip/catalog" sx={{ px: 3, ...getMenuItemStyle('/trip/catalog'), ...menuItemNoActiveStyle }}>Catalog</MenuItem>
          <MenuItem component={NavLink} to="/trip/reports" sx={{ px: 3, ...getMenuItemStyle('/trip/reports'), ...menuItemNoActiveStyle }}>Reports</MenuItem>
        </Menu>
      )}

      {/* Parcel Management submenu */}
      {user && (
      <Menu 
        anchorEl={refParcel.current} 
        onClose={handleCloseParcel} 
        open={isOpenParcel}
        keepMounted={false}
        disableScrollLock
        disablePortal={false}
        PaperProps={{
          sx: {
            marginTop: 2,
            zIndex: theme => theme.zIndex.drawer + 100,
            '& .MuiList-root': {
              padding: 1
            }
          }
        }}
      >
        <MenuItem component={NavLink} to="/parcel/shipments" sx={{ px: 3, ...getMenuItemStyle('/parcel/shipments'), ...menuItemNoActiveStyle }}>Shipments</MenuItem>
        <MenuItem component={NavLink} to="/parcel/orders" sx={{ px: 3, ...getMenuItemStyle('/parcel/orders'), ...menuItemNoActiveStyle }}>Orders</MenuItem>
        <MenuItem component={NavLink} to="/parcel/parcels" sx={{ px: 3, ...getMenuItemStyle('/parcel/parcels'), ...menuItemNoActiveStyle }}>Parcels</MenuItem>
        <MenuItem component={NavLink} to="/parcel/movements" sx={{ px: 3, ...getMenuItemStyle('/parcel/movements'), ...menuItemNoActiveStyle }}>Movements</MenuItem>
        <MenuItem component={NavLink} to="/parcel/relations" sx={{ px: 3, ...getMenuItemStyle('/parcel/relations'), ...menuItemNoActiveStyle }}>Relations</MenuItem>
        <MenuItem component={NavLink} to="/parcel/catalog" sx={{ px: 3, ...getMenuItemStyle('/parcel/catalog'), ...menuItemNoActiveStyle }}>Catalog</MenuItem>
        <MenuItem component={NavLink} to="/parcel/reports" sx={{ px: 3, ...getMenuItemStyle('/parcel/reports'), ...menuItemNoActiveStyle }}>Reports</MenuItem>
      </Menu>
      )}

      {/* Parts Management submenu */}
      {user && (
        <Menu 
        anchorEl={refParts?.current} 
        onClose={handleCloseParts} 
        open={isOpenParts}
        keepMounted={false}
        disableScrollLock
        disablePortal={false}
        PaperProps={{
          sx: {
            marginTop: 2,
            zIndex: theme => theme.zIndex.drawer + 100,
            '& .MuiList-root': {
              padding: 1
            }
          }
        }}
      >
          
        <MenuItem component={NavLink} to="/parts/assemblies" sx={{ px: 3, ...getMenuItemStyle('/parts/assemblies'), ...menuItemNoActiveStyle }}>Assemblies</MenuItem>
        <MenuItem component={NavLink} to="/parts/components" sx={{ px: 3, ...getMenuItemStyle('/parts/components'), ...menuItemNoActiveStyle }}>Components</MenuItem>
        <MenuItem component={NavLink} to="/parts/catalog" sx={{ px: 3, ...getMenuItemStyle('/parts/catalog'), ...menuItemNoActiveStyle }}>Catalog</MenuItem>
        <MenuItem component={NavLink} to="/parts/reports" sx={{ px: 3, ...getMenuItemStyle('/parts/reports'), ...menuItemNoActiveStyle }}>Reports</MenuItem>
      </Menu>
      )}

      {/* Budget Management submenu */}
      {user && (
        <Menu 
        anchorEl={refBudget.current} 
        onClose={handleCloseBudget} 
        open={isOpenBudget}
        keepMounted={false}
        disableScrollLock
        disablePortal={false}
        PaperProps={{
          sx: {
            marginTop: 2,
            zIndex: theme => theme.zIndex.drawer + 100,
            '& .MuiList-root': {
              padding: 1
            }
          }
        }}
      >
        <MenuItem component={NavLink} to="/budget/dashboard" sx={{ px: 3, ...getMenuItemStyle('/budget/dashboard'), ...menuItemNoActiveStyle }}>Dashboard</MenuItem>
        <MenuItem component={NavLink} to="/budget/transactions" sx={{ px: 3, ...getMenuItemStyle('/budget/transactions'), ...menuItemNoActiveStyle }}>Transactions</MenuItem>
        <MenuItem component={NavLink} to="/budget/accounts" sx={{ px: 3, ...getMenuItemStyle('/budget/accounts'), ...menuItemNoActiveStyle }}>Accounts</MenuItem>
        <MenuItem component={NavLink} to="/budget/currencies" sx={{ px: 3, ...getMenuItemStyle('/budget/currencies'), ...menuItemNoActiveStyle }}>Currencies</MenuItem>
        <MenuItem component={NavLink} to="/budget/installments" sx={{ px: 3, ...getMenuItemStyle('/budget/installments'), ...menuItemNoActiveStyle }}>Installments</MenuItem>
        <MenuItem component={NavLink} to="/budget/exchanges" sx={{ px: 3, ...getMenuItemStyle('/budget/exchanges'), ...menuItemNoActiveStyle }}>Exchanges</MenuItem>
        <MenuItem component={NavLink} to="/budget/limits" sx={{ px: 3, ...getMenuItemStyle('/budget/limits'), ...menuItemNoActiveStyle }}>Limits</MenuItem>
        <MenuItem component={NavLink} to="/budget/payments" sx={{ px: 3, ...getMenuItemStyle('/budget/payments'), ...menuItemNoActiveStyle }}>Payments</MenuItem>
        <MenuItem component={NavLink} to="/budget/debt" sx={{ px: 3, ...getMenuItemStyle('/budget/debt'), ...menuItemNoActiveStyle }}>Debt</MenuItem>
        <MenuItem component={NavLink} to="/budget/catalog" sx={{ px: 3, ...getMenuItemStyle('/budget/catalog'), ...menuItemNoActiveStyle }}>Catalog</MenuItem>
        <MenuItem component={NavLink} to="/budget/settings" sx={{ px: 3, ...getMenuItemStyle('/budget/settings'), ...menuItemNoActiveStyle }}>Settings</MenuItem>
        <MenuItem component={NavLink} to="/budget/reports" sx={{ px: 3, ...getMenuItemStyle('/budget/reports'), ...menuItemNoActiveStyle }}>Reports</MenuItem>
      </Menu>
      )}

      {/* Stocks Management submenu */}
      {user && (
        <Menu 
        anchorEl={refStocks.current} 
        onClose={handleCloseStocks} 
        open={isOpenStocks}
        keepMounted={false}
        disableScrollLock
        disablePortal={false}
        PaperProps={{
          sx: {
            marginTop: 2,
            zIndex: theme => theme.zIndex.drawer + 100,
            '& .MuiList-root': {
              padding: 1
            }
          }
        }}
      >
    <MenuItem component={NavLink} to="/stocks/dashboard" sx={{ px: 3, ...getMenuItemStyle('/stocks/dashboard'), ...menuItemNoActiveStyle }}>Dashboard</MenuItem>
    <MenuItem component={NavLink} to="/stocks/wallet" sx={{ px: 3, ...getMenuItemStyle('/stocks/wallet'), ...menuItemNoActiveStyle }}>Wallet</MenuItem>
    <MenuItem component={NavLink} to="/stocks/results" sx={{ px: 3, ...getMenuItemStyle('/stocks/results'), ...menuItemNoActiveStyle }}>Results</MenuItem>
    <MenuItem component={NavLink} to="/stocks/catalog" sx={{ px: 3, ...getMenuItemStyle('/stocks/catalog'), ...menuItemNoActiveStyle }}>Catalog</MenuItem>
    <MenuItem component={NavLink} to="/stocks/reports" sx={{ px: 3, ...getMenuItemStyle('/stocks/reports'), ...menuItemNoActiveStyle }}>Reports</MenuItem>
      </Menu>
      )}

      {/* Fitness Training submenu */}
      {user && (
      <Menu 
        anchorEl={refTraining.current} 
        onClose={handleCloseTraining} 
        open={isOpenTraining}
        keepMounted={false}
        disableScrollLock
        disablePortal={false}
        PaperProps={{
          sx: {
            marginTop: 2,
            zIndex: theme => theme.zIndex.drawer + 100,
            '& .MuiList-root': {
              padding: 1
            }
          }
        }}
      >
        <MenuItem component={NavLink} to="/fitness/workouts" sx={{ px: 3, ...getMenuItemStyle('/fitness/workouts'), ...menuItemNoActiveStyle }}>Workouts</MenuItem>
        <MenuItem component={NavLink} to="/fitness/routines" sx={{ px: 3, ...getMenuItemStyle('/fitness/routines'), ...menuItemNoActiveStyle }}>Routines</MenuItem>
        <MenuItem component={NavLink} to="/fitness/sets" sx={{ px: 3, ...getMenuItemStyle('/fitness/sets'), ...menuItemNoActiveStyle }}>Sets</MenuItem>
        <MenuItem component={NavLink} to="/fitness/progressions" sx={{ px: 3, ...getMenuItemStyle('/fitness/progressions'), ...menuItemNoActiveStyle }}>Progressions</MenuItem>
        <MenuItem component={NavLink} to="/fitness/exercises" sx={{ px: 3, ...getMenuItemStyle('/fitness/exercises'), ...menuItemNoActiveStyle }}>Exercises</MenuItem>
        <MenuItem component={NavLink} to="/fitness/catalog" sx={{ px: 3, ...getMenuItemStyle('/fitness/catalog'), ...menuItemNoActiveStyle }}>Catalog</MenuItem>
        <MenuItem component={NavLink} to="/fitness/reports" sx={{ px: 3, ...getMenuItemStyle('/fitness/reports'), ...menuItemNoActiveStyle }}>Reports</MenuItem>
      </Menu>
      )}
    </>
  );
}

export default HeaderMenu;
